import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../store/store";
import { toggleSymptom, clearSymptoms, addHistory } from "../store/aiAgentSlice";

const symptomsList = [
  "Dizziness",
  "Fatigue",
  "Headache",
  "Thirst",
  "Blurred Vision",
];

const getRiskLevel = (count: number) => {
  if (count >= 4) return { level: "High", color: "danger" };
  if (count >= 2) return { level: "Moderate", color: "warning" };
  if (count === 1) return { level: "Low", color: "success" };
  return { level: "None", color: "secondary" };
};

const getInsightMessage = (selected: string[]) => {
  if (selected.length === 0)
    return "Select symptoms to receive AI-driven insights.";
  if (selected.includes("Blurred Vision") && selected.includes("Thirst"))
    return "These symptoms may indicate glucose fluctuation. Continue tracking daily.";
  if (selected.includes("Fatigue") && selected.includes("Dizziness"))
    return "Early-stage signs detected. Maintain diet and activity logs.";
  return "AI recommends continuous logging and monitoring trends over time.";
};

const DiabetesAISymptomChecker: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const selectedSymptoms = useSelector(
    (state: RootState) => state.aiAgent.selectedSymptoms
  );
  const history = useSelector((state: RootState) => state.aiAgent.history);
  const [analyzing, setAnalyzing] = useState(false);

  const handleToggleSymptom = (symptom: string) => {
    dispatch(toggleSymptom(symptom));
    setAnalyzing(true);

    setTimeout(() => {
      setAnalyzing(false);

      dispatch(
        addHistory({
          timestamp: new Date().toLocaleString(),
          symptoms: [...selectedSymptoms],
        })
      );
    }, 1000);
  };

  const risk = getRiskLevel(selectedSymptoms.length);
  const insight = getInsightMessage(selectedSymptoms);

  const symptomFrequency: Record<string, number> = {};
  history.forEach((session) => {
    session.symptoms.forEach((s) => {
      symptomFrequency[s] = (symptomFrequency[s] || 0) + 1;
    });
  });

  const maxFrequency = Math.max(...Object.values(symptomFrequency), 1);

  return (
    <div className="container py-5">

      {/* BIG DISCLAIMER AT TOP */}
      <div className="alert alert-warning text-center fs-5 fw-bold py-4 rounded-3 mb-4">
        ⚠️ Disclaimer
        <div className="fw-normal mt-2">
          This Diabetes AI Agent is for <strong>educational purposes only</strong>.  
          It does not provide a medical diagnosis.  
          Please consult your healthcare  provider for professional advice regarding any symptoms or health concerns.
        </div>
      </div>

      <h1 className="mb-2">Diabetes AI Agent – Beta</h1>
      <p className="text-muted mb-4">
        Our AI agent is currently in <strong>beta phase</strong>.
        Select your symptoms below to receive AI-driven analysis in real-time.
      </p>

      {/* Symptoms Selection */}
      <h5>Select your symptoms:</h5>
      <div className="mb-3">
        {symptomsList.map((symptom) => (
          <button
            key={symptom}
            className={`btn me-2 mb-2 ${
              selectedSymptoms.includes(symptom)
                ? "btn-primary"
                : "btn-outline-primary"
            }`}
            onClick={() => handleToggleSymptom(symptom)}
          >
            {symptom}
          </button>
        ))}
      </div>

      {/* AI Engine Status */}
      {analyzing && (
        <div className="mb-3 d-flex align-items-center">
          <div className="spinner-border text-primary me-2" role="status"></div>
          <span className="text-primary fw-bold">
            Analyzing selected symptoms...
          </span>
        </div>
      )}

      {/* Risk Level */}
      <div className="mb-4">
        <h6>Risk Level:</h6>
        <span className={`badge bg-${risk.color} p-2 fs-6`}>
          {risk.level}
        </span>
      </div>

      {/* AI Insights */}
      <div className="mb-4">
        <h6>AI Insight:</h6>
        <p className="text-info fst-italic">{insight}</p>
      </div>

      {/* Selected Symptoms */}
      <div className="mt-4">
        <h6>Selected Symptoms:</h6>
        {selectedSymptoms.length > 0 ? (
          <ul className="list-group list-group-flush">
            {selectedSymptoms.map((s) => (
              <li key={s} className="list-group-item">
                {s}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-muted">No symptoms selected yet.</p>
        )}
      </div>

      {/* Clear Symptoms */}
      <button
        className="btn btn-danger mt-3"
        onClick={() => dispatch(clearSymptoms())}
      >
        Clear Symptoms
      </button>

      {/* Symptom Frequency Visualization */}
      {history.length > 0 && (
        <div className="mt-4">
          <h6>Symptom Frequency Trends</h6>
          <div>
            {Object.keys(symptomFrequency).map((symptom) => (
              <div key={symptom} className="mb-2">
                <strong>{symptom}</strong>
                <div
                  style={{
                    height: "20px",
                    width: `${(symptomFrequency[symptom] / maxFrequency) * 100}%`,
                    backgroundColor: "#0d6efd",
                    borderRadius: "4px",
                    transition: "width 0.5s",
                  }}
                />
                <small className="text-muted ms-2">
                  {symptomFrequency[symptom]} time
                  {symptomFrequency[symptom] > 1 ? "s" : ""}
                </small>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DiabetesAISymptomChecker;
